import { Component, Prop, h, Host, Element } from '@stencil/core';

/**
 * SignNow wrapper for the testimonials-type-4 carousel component.
 * Displays testimonials in a slider format with content on the left and decoration image on the right.
 * Heading and subtitle are provided as slotted elements for inline editor support.
 *
 * @product signnow
 * @category SignNow
 * @source reviews
 * @slot heading - {required}, <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @slot subtitle - <me-text> - Section subtitle
 * @slot decoration - {required}, <sn-reviews-decoration-type-1>
 * @slot item - {required}, {multiple}, {min-items: 1}, <div data-header="..." data-stars="5" data-author="..." data-review-raw-html="..." data-platform-href="..." data-platform-href-title="..."> - Review items (read via @Element in componentWillLoad)
 * @version 2.0.0
 * @example
 *   <sn-testimonials-type-1>
 *     <me-heading slot="heading" variant="2" tag="h2" align="center">What our customers say</me-heading>
 *     <me-text slot="subtitle" variant="medium" align="center">Read testimonials from our satisfied customers</me-text>
 *     <div slot="item"
 *       data-header="Excellent resource"
 *       data-stars="5"
 *       data-author="John Doe"
 *       data-review-raw-html="&lt;p&gt;Great product!&lt;/p&gt;"
 *       data-platform-href="https://example.com/review"
 *       data-platform-href-title="Read full review"
 *     ></div>
 *     <sn-reviews-decoration-type-1 slot="decoration"></sn-reviews-decoration-type-1>
 *   </sn-testimonials-type-1>
 */
@Component({
  tag: 'sn-testimonials-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnTestimonialsType1 {
  @Element() el!: HTMLElement;

  /**
   * Color mode for the component.
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Enable/disable carousel autoplay
   * @default false
   */
  @Prop() autoplay: boolean = false;

  /**
   * Autoplay interval in milliseconds
   * @default 5000
   */
  @Prop() autoplayInterval: number = 5000;

  /**
   * ARIA label for carousel navigation container
   * @default "Carousel navigation"
   */
  @Prop() ariaLabelCarouselNavigation: string = 'Carousel navigation';

  /**
   * ARIA label template for slide navigation dots. Use {index} placeholder for slide number (1-based).
   * @default "Go to slide {index}"
   */
  @Prop() ariaLabelSlide: string = 'Go to slide {index}';

  /**
   * ARIA label for previous slide button
   * @default "Previous slide"
   */
  @Prop() ariaLabelPrevious: string = 'Previous slide';

  /**
   * ARIA label for next slide button
   * @default "Next slide"
   */
  @Prop() ariaLabelNext: string = 'Next slide';

  /**
   * ARIA label template for rating. Use {rating} placeholder for rating value and {max} for maximum rating.
   * @default "Rated {rating} out of {max} stars"
   */
  @Prop() ariaLabelRating: string = 'Rated {rating} out of {max} stars';

  /**
   * Maximum rating value for Schema.org and ARIA labels
   * @default "5"
   */
  @Prop() maxRating: string = '5';

  private items: any[] = [];

  componentWillLoad() {
    const slottedChildren = Array.from(this.el.querySelectorAll('[slot="item"]'));
    this.items = slottedChildren.map(child => ({
      header: child.getAttribute('data-header') || '',
      stars: Number(child.getAttribute('data-stars') || 5),
      author: child.getAttribute('data-author') || '',
      reviewRawHtml: child.getAttribute('data-review-raw-html') || '',
      reviewPlatform: {
        href: child.getAttribute('data-platform-href') || '',
        hrefTitle: child.getAttribute('data-platform-href-title') || '',
      },
    }));
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <testimonials-type-4
          items={this.items as any}
          autoplay={this.autoplay}
          autoplayInterval={this.autoplayInterval}
          ariaLabelCarouselNavigation={this.ariaLabelCarouselNavigation}
          ariaLabelSlide={this.ariaLabelSlide}
          ariaLabelPrevious={this.ariaLabelPrevious}
          ariaLabelNext={this.ariaLabelNext}
          ariaLabelRating={this.ariaLabelRating}
          maxRating={this.maxRating}
        >
          <slot name="heading" slot="heading"></slot>
          <slot name="subtitle" slot="subtitle"></slot>
          <slot name="decoration" slot="decoration"></slot>
        </testimonials-type-4>
      </Host>
    );
  }
}
